import { useMutation, useQueryClient } from "@tanstack/react-query";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { pollForFileStatus } from "@/utils/vector-store-polling";

/**
 * Delete a file from a vector store
 */
export const useDeleteVectorStoreFile = () => {
  const auth = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;
  const qc = useQueryClient();

  return useMutation(
    async (params: { vector_store_id: string; file_id: string; onConfirmed?: () => void }) => {
      const response = await fetch(`${API_URL}/api/dashboard/v2/vector-store/file/delete`, {
        method: "POST",
        headers: jsonAuthHeaders(auth!.userToken!),
        body: JSON.stringify({
          vector_store_id: params.vector_store_id,
          file_id: params.file_id,
          workspace_id: workspaceId,
        }),
      }).then(handleResponse);

      // Check if response has success: false
      if (response && typeof response === "object" && "success" in response) {
        if (response.success === false) {
          const errorMessage =
            (response as { message?: string }).message || "Failed to delete file from vector store";
          throw new Error(errorMessage);
        }
      }

      return response;
    },
    {
      onSuccess: (_data, variables) => {
        if (workspaceId) {
          pollForFileStatus({
            qc,
            vectorStoreId: variables.vector_store_id,
            fileId: variables.file_id,
            auth,
            workspaceId,
            checkCondition: fileIds => !fileIds.includes(variables.file_id),
            queryKeys: {
              files: ["vector-store-files", variables.vector_store_id, workspaceId],
              stores: ["vector-stores", workspaceId],
            },
            onConfirmed: variables.onConfirmed,
            errorMessage: "Error polling for file deletion",
          });
        }
      },
    },
  );
};
